import ImageLightboxGallery from '@/components/media/ImageLightboxGallery'
import { createLightboxPreviewItem } from '@/components/media/ImageLightboxPreviewTrigger'
import { Badge } from '@/components/ui/badge'
import { DetailWell } from '@/components/patterns/detail-page'
import { getSafeDayjs } from '@/components/orders/delivery-share.utils'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import { cn } from '@/lib/utils'
import { Link } from '@tanstack/react-router'
import { CheckCircle2, RotateCcw, TriangleAlert, type LucideIcon } from 'lucide-react'
import type { ConfigType } from 'dayjs'
import type { ReactElement } from 'react'
import type {
  DeliveryAttempt,
  DeliveryShareStatus,
} from '@/lib/services/deliveries/deliveries.types'

export type DeliveryTimelineAttempt = Partial<DeliveryAttempt>
type DeliveryRoute = NonNullable<DeliveryAttempt['delivery_share']>
type ViewableDeliveryRoute = DeliveryRoute & { status: Exclude<DeliveryShareStatus, 'revoked'> }
type DeliveryRouteLink = ReturnType<
  ReturnType<typeof usePathHelper>['deliveryRoutes']['show']
>['link']

const viewableRouteStatuses: readonly (DeliveryShareStatus | undefined)[] = [
  'active',
  'completed',
  'expired',
]

function isViewableRoute(route: DeliveryAttempt['delivery_share']): route is ViewableDeliveryRoute {
  return viewableRouteStatuses.includes(route?.status)
}

function getAttemptAppearance(attempt: DeliveryTimelineAttempt): {
  icon: LucideIcon
  tone: 'neutral' | 'success' | 'warning'
  markerClass: string
} {
  if (attempt?.is_reversed) {
    return {
      icon: RotateCcw,
      tone: 'neutral',
      markerClass:
        'border-muted-foreground/20 bg-muted text-muted-foreground shadow-muted-foreground/5 dark:border-border/45 dark:bg-elevated',
    }
  }

  if (attempt?.outcome === 'delivered') {
    return {
      icon: CheckCircle2,
      tone: 'success',
      markerClass:
        'border-status-success-border bg-status-success-background text-status-success-foreground shadow-status-success-foreground/10 dark:border-border/45 dark:bg-elevated',
    }
  }

  return {
    icon: TriangleAlert,
    tone: 'warning',
    markerClass:
      'border-status-warning-border bg-status-warning-background text-status-warning-foreground shadow-status-warning-foreground/10 dark:border-border/45 dark:bg-elevated',
  }
}

function getAttemptTitle(attempt: DeliveryTimelineAttempt): string {
  return (
    attempt?.summary_label ||
    attempt?.outcome_label ||
    `Attempt #${getAttemptDisplayNumber(attempt)}`
  )
}

function getAttemptDisplayNumber(attempt: DeliveryTimelineAttempt): number {
  return Number(attempt?.route_attempt_number || attempt?.attempt_number || 0)
}

function formatAttemptedAt(attemptedAt: ConfigType): string | null {
  const parsedAttemptedAt = getSafeDayjs(attemptedAt)

  return parsedAttemptedAt ? parsedAttemptedAt.format('MMM D, YYYY [at] h:mm A') : null
}

function getAttemptSortValue(attempt: DeliveryTimelineAttempt): number {
  const attemptedAt = getSafeDayjs(attempt?.attempted_at)

  return attemptedAt ? attemptedAt.valueOf() : Number.MAX_SAFE_INTEGER
}

function AttemptRouteLink({
  route,
  link,
}: {
  route: DeliveryRoute
  link: DeliveryRouteLink | null
}): ReactElement {
  return link ? (
    <ButtonLink label={`Route #${route.id}`} link={link} />
  ) : (
    <Typography variant="supporting" className="inline-flex items-center">
      Route #{route.id} · {route.status}
    </Typography>
  )
}

export function DeliveryAttemptTimeline({
  attempts = [],
  emptyMessage = 'No delivery attempts logged yet.',
  showRoute = false,
}: {
  attempts?: readonly DeliveryTimelineAttempt[]
  emptyMessage?: string
  showRoute?: boolean
}): ReactElement {
  const path = usePathHelper()
  const connectorClass = 'bg-border/80'

  if (!attempts.length) {
    return (
      <DetailWell tone="periwinkle" className="px-4 py-5">
        <Typography variant="supporting">{emptyMessage}</Typography>
      </DetailWell>
    )
  }

  const orderedAttempts = [...attempts].sort((left, right) => {
    const leftAttemptNumber = getAttemptDisplayNumber(left)
    const rightAttemptNumber = getAttemptDisplayNumber(right)

    if (leftAttemptNumber !== rightAttemptNumber) {
      return leftAttemptNumber - rightAttemptNumber
    }

    const attemptedAtDifference = getAttemptSortValue(left) - getAttemptSortValue(right)

    if (attemptedAtDifference !== 0) {
      return attemptedAtDifference
    }

    return Number(left?.id || 0) - Number(right?.id || 0)
  })

  return (
    <div className="space-y-3">
      {orderedAttempts.map((attempt, index) => {
        const isFirstAttempt = index === 0
        const route = attempt.delivery_share
        const routeLink = isViewableRoute(route) ? path.deliveryRoutes.show(route.id).link : null
        const isLastAttempt = index === orderedAttempts.length - 1
        const { icon: AttemptIcon, tone, markerClass } = getAttemptAppearance(attempt)
        const attemptedAtLabel = formatAttemptedAt(attempt.attempted_at)
        const attemptDisplayNumber = getAttemptDisplayNumber(attempt)
        const proofPhotoItem = createLightboxPreviewItem({
          media: attempt.proof_photo,
          alt: `Proof photo for attempt #${attemptDisplayNumber}`,
          caption: `Proof photo for attempt #${attemptDisplayNumber}`,
        })

        return (
          <div
            key={attempt.id}
            className="grid grid-cols-[2rem_minmax(0,1fr)] gap-4 sm:grid-cols-[2rem_minmax(0,1fr)_auto]"
          >
            <div className="relative">
              {!isFirstAttempt ? (
                <span
                  className={cn(
                    'absolute bottom-1/2 left-1/2 top-[-0.75rem] z-0 w-px -translate-x-1/2',
                    connectorClass
                  )}
                />
              ) : null}
              {!isLastAttempt ? (
                <span
                  className={cn(
                    'absolute bottom-[-0.75rem] left-1/2 top-1/2 z-0 w-px -translate-x-1/2',
                    connectorClass
                  )}
                />
              ) : null}
              <span
                className={cn(
                  'absolute left-1/2 top-1/2 z-10 flex size-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border bg-background shadow-sm',
                  markerClass
                )}
              >
                <AttemptIcon className="size-4" />
              </span>
            </div>

            <DetailWell
              tone={tone}
              className={cn('min-w-0 space-y-3 px-4 py-4', attempt.is_reversed && 'bg-muted/35')}
            >
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Typography variant="cardTitle">{getAttemptTitle(attempt)}</Typography>
                  {attempt.is_reversed ? <Badge variant="outline">Undone</Badge> : null}
                </div>

                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <Typography variant="label" tone="muted">
                    Attempt #{attemptDisplayNumber}
                  </Typography>
                  {showRoute && attempt.delivery_share?.id ? (
                    <div className="inline-flex items-center gap-2">
                      <span className="size-1 rounded-full bg-border" />
                      <AttemptRouteLink route={attempt.delivery_share} link={routeLink} />
                    </div>
                  ) : null}
                  {attemptedAtLabel ? (
                    <Typography variant="supporting" className="sm:hidden">
                      {attemptedAtLabel}
                    </Typography>
                  ) : null}
                </div>
              </div>

              {attempt.note ? (
                <p className="max-w-2xl text-sm leading-6 text-foreground">{attempt.note}</p>
              ) : null}

              {proofPhotoItem ? (
                <ImageLightboxGallery items={[proofPhotoItem]} className="contents">
                  {({ getTriggerProps }) => (
                    <a
                      {...getTriggerProps(0, {
                        'aria-label': `Preview proof photo for attempt #${attemptDisplayNumber}`,
                      })}
                      className="flex w-fit max-w-full flex-wrap items-center gap-3 rounded-xl bg-card/90 px-3 py-2 ring-1 ring-border/50 transition-colors hover:bg-background/80 hover:ring-border/70 dark:bg-white/[0.04] dark:hover:bg-white/[0.07]"
                    >
                      <img
                        src={proofPhotoItem.url}
                        alt={`Proof photo for attempt #${attemptDisplayNumber}`}
                        className="size-20 rounded-lg object-cover ring-1 ring-border/60"
                        loading="lazy"
                      />
                      <div className="space-y-1">
                        <Typography variant="label" className="block">
                          Proof photo
                        </Typography>
                        <Typography variant="supporting" className="block">
                          Preview full size
                        </Typography>
                      </div>
                      <span className="pswp-caption-content hidden">{proofPhotoItem.caption}</span>
                    </a>
                  )}
                </ImageLightboxGallery>
              ) : null}
            </DetailWell>

            {attemptedAtLabel ? (
              <div className="hidden min-w-[11.5rem] self-center text-right sm:block">
                <Typography variant="supporting" className="block">
                  {attemptedAtLabel}
                </Typography>
              </div>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

function ButtonLink({ label, link }: { label: string; link: DeliveryRouteLink }): ReactElement {
  return (
    <Link {...link} className="inline-flex text-sm font-medium text-interaction hover:underline">
      {label}
    </Link>
  )
}
